/**
 * Strategy Validation Service - TypeScript
 */

import { Strategy, MarketSettings, ExecutionSettings, Conditions, PositionManager, ValidationResult } from '../types/blockly';

export interface ApiPayload {
  request: string;
  symbol: string;
  contract_type: string;
  stake: number;
  duration: number;
  duration_unit: string;
  barrier?: number;
  barrier_low?: number;
  barrier_high?: number;
  digit_target?: number;
  digit_low?: number;
  digit_high?: number;
}

export interface ApiValidationResult {
  valid: boolean;
  errors: string[];
}

export class ValidationService {
  /**
   * Validate complete strategy
   */
  validateStrategy(strategy: Strategy): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required blocks
    if (!strategy.market) {
      errors.push('Market settings are required');
    }

    if (!strategy.execution) {
      errors.push('Execution settings are required');
    }

    // Validate market settings
    if (strategy.market) {
      const marketErrors = this.validateMarket(strategy.market);
      errors.push(...marketErrors);
    }

    // Validate execution settings
    if (strategy.execution) {
      const execErrors = this.validateExecution(strategy.execution);
      errors.push(...execErrors);
    }

    // Validate conditions
    if (strategy.conditions) {
      const conditionErrors = this.validateConditions(strategy.conditions);
      errors.push(...conditionErrors);
      const conditionWarnings = this.validateConditionCompatibility(strategy.conditions);
      warnings.push(...conditionWarnings);
    }

    if (strategy.indicators) {
      const indicatorErrors = this.validateIndicators(strategy.indicators);
      errors.push(...indicatorErrors);
    }

    if (strategy.restart) {
      const restartErrors = this.validateRestart(strategy.restart);
      errors.push(...restartErrors);
    }

    // Validate position manager
    if (strategy.positionManager) {
      const posErrors = this.validatePositionManager(strategy.positionManager);
      warnings.push(...posErrors);
    }

    // Check for potential issues
    if (strategy.market && strategy.execution) {
      // Check if stake is reasonable
      if (strategy.execution.stake > 1000) {
        warnings.push('High stake amount - consider risk management');
      }

      // Check for stop loss
      if (!strategy.positionManager) {
        warnings.push('No position manager - consider adding stop loss');
      }

      // Check for take profit
      if (strategy.positionManager && strategy.positionManager.takeProfit === 0) {
        warnings.push('Take profit is set to 0 - consider setting a profit target');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      hasWarnings: warnings.length > 0,
    };
  }

  /**
   * Validate market settings
   */
  private validateMarket(market: MarketSettings): string[] {
    const errors: string[] = [];

    if (!market.symbol) {
      errors.push('Symbol is required');
    }

    if (!market.category) {
      errors.push('Contract category is required');
    }

    if (!market.contractType) {
      errors.push('Contract type is required');
    }

    // Validate barriers based on contract type
    if (this.requiresBarrier(market.contractType)) {
      if (market.barrier === undefined || market.barrier === null) {
        errors.push(`Barrier is required for ${market.contractType}`);
      }
    }

    if (this.requiresDoubleBarrier(market.contractType)) {
      if (market.barrierLow === undefined || market.barrierHigh === undefined) {
        errors.push(`Double barrier is required for ${market.contractType}`);
      }
      if (market.barrierLow !== undefined && market.barrierHigh !== undefined) {
        if (market.barrierLow >= market.barrierHigh) {
          errors.push('Lower barrier must be less than upper barrier');
        }
      }
    }

    if (this.requiresDigit(market.contractType)) {
      if (market.digitTarget === undefined || market.digitTarget === null) {
        errors.push(`Digit target is required for ${market.contractType}`);
      }
      if (market.digitTarget !== undefined) {
        if (market.digitTarget < 0 || market.digitTarget > 9) {
          errors.push('Digit target must be between 0 and 9');
        }
      }
    }

    if (this.requiresDigitRange(market.contractType)) {
      if (market.digitLow === undefined || market.digitHigh === undefined) {
        errors.push(`Digit range is required for ${market.contractType}`);
      }
      if (market.digitLow !== undefined && market.digitHigh !== undefined) {
        if (market.digitLow >= market.digitHigh) {
          errors.push('Digit low must be less than digit high');
        }
        if (market.digitLow < 0 || market.digitLow > 8) {
          errors.push('Digit low must be between 0 and 8');
        }
        if (market.digitHigh < 1 || market.digitHigh > 9) {
          errors.push('Digit high must be between 1 and 9');
        }
      }
    }

    return errors;
  }

  /**
   * Validate execution settings
   */
  private validateExecution(execution: ExecutionSettings): string[] {
    const errors: string[] = [];

    if (!execution.stake || execution.stake <= 0) {
      errors.push('Stake must be greater than 0');
    }

    if (!execution.duration || execution.duration <= 0) {
      errors.push('Duration must be greater than 0');
    }

    if (!execution.durationUnit) {
      errors.push('Duration unit is required');
    }

    if (execution.stake > 50000) {
      errors.push('Stake exceeds maximum limit (50000)');
    }

    if (execution.duration > 1000) {
      errors.push('Duration exceeds maximum limit (1000)');
    }

    return errors;
  }

  /**
   * Validate conditions
   */
  private validateConditions(conditions: Conditions): string[] {
    const errors: string[] = [];
    const noValueConditions = new Set([
      'ALWAYS',
      'HAS_POSITION',
      'NO_POSITION',
    ]);
    const numericConditionTypes = new Set([
      'PRICE_GT',
      'PRICE_LT',
      'CURRENT_TICK',
      'TICK_COUNT',
      'LOSS_THRESHOLD',
      'PROFIT_THRESHOLD',
      'SELL_BY_COUNT_DOWN',
      'SELL_BY_TAKE_PROFIT',
      'STOP_LOSS_HIT',
      'TAKE_PROFIT_HIT',
      'DURATION_ELAPSED',
    ]);
    const timeConditionTypes = new Set(['TIME_OF_DAY']);

    const validateCondition = (condition: { type: string; value: string; value2?: string } | null | undefined, prefix: string): void => {
      if (!condition) return;
      const type = String(condition.type ?? '').trim().toUpperCase();
      const value = String(condition.value ?? '').trim();
      const value2 = String(condition.value2 ?? '').trim();

      if (!type) {
        errors.push(`${prefix} condition type is required`);
        return;
      }

      if (noValueConditions.has(type)) {
        return;
      }

      if (type === 'PRICE_BETWEEN') {
        if (!value || !value2) {
          errors.push(`${prefix} condition requires two values`);
          return;
        }
        const low = Number(value);
        const high = Number(value2);
        if (!Number.isFinite(low) || !Number.isFinite(high)) {
          errors.push(`${prefix} condition values must be numbers`);
          return;
        }
        if (low >= high) {
          errors.push(`${prefix} condition lower value must be less than upper value`);
        }
        return;
      }

      if (timeConditionTypes.has(type)) {
        if (!value) {
          errors.push(`${prefix} condition value is required`);
          return;
        }
        if (!/^\d{1,2}:\d{2}$/.test(value)) {
          errors.push(`${prefix} condition time must be in HH:MM format`);
        }
        return;
      }

      if (!value) {
        errors.push(`${prefix} condition value is required`);
        return;
      }

      if (numericConditionTypes.has(type)) {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          errors.push(`${prefix} condition value must be a number`);
        }
      }
    };

    const entryCondition = conditions.entry ?? conditions.purchase ?? null;
    const exitCondition = conditions.exit ?? conditions.sell ?? null;

    validateCondition(entryCondition as { type: string; value: string; value2?: string } | null | undefined, 'Entry');
    validateCondition(exitCondition as { type: string; value: string; value2?: string } | null | undefined, 'Exit');

    const management = conditions.management;
    if (management) {
      if (management.initialStake < 0.5) {
        errors.push('Management initial stake must be at least 0.5');
      }
      if (management.multiplier < 1) {
        errors.push('Management multiplier must be at least 1');
      }
      if (management.maxStake < management.initialStake) {
        errors.push('Management max stake must be greater than or equal to initial stake');
      }
      if (management.profitThreshold < 0) {
        errors.push('Management profit threshold cannot be negative');
      }
      if (management.lossThreshold < 0) {
        errors.push('Management loss threshold cannot be negative');
      }
      if (management.repeatRuns !== undefined && management.repeatRuns !== null && management.repeatRuns < 1) {
        errors.push('Management repeat runs must be at least 1');
      }
    }

    return errors;
  }

  /**
   * Warn about condition combinations that are likely confusing or contradictory.
   */
  private validateConditionCompatibility(conditions: Conditions): string[] {
    const warnings: string[] = [];
    const entry = conditions.entry ?? conditions.purchase ?? null;
    const exit = conditions.exit ?? conditions.sell ?? null;
    const entryType = String(entry?.type ?? '').trim().toUpperCase();
    const exitType = String(exit?.type ?? '').trim().toUpperCase();
    const timeTypes = new Set(['TIME_OF_DAY', 'DURATION_ELAPSED']);
    const thresholdTypes = new Set(['LOSS_THRESHOLD', 'PROFIT_THRESHOLD', 'STOP_LOSS_HIT', 'TAKE_PROFIT_HIT']);
    const hasManagementThresholds = Boolean(
      conditions.management &&
      (conditions.management.lossThreshold != null || conditions.management.profitThreshold != null)
    );

    const entryIsTime = timeTypes.has(entryType);
    const exitIsTime = timeTypes.has(exitType);
    const entryIsThreshold = thresholdTypes.has(entryType);
    const exitIsThreshold = thresholdTypes.has(exitType);

    if (!entryType && !exitType) {
      return warnings;
    }

    if (entryType === 'ALWAYS' && exitType === 'ALWAYS') {
      warnings.push('Entry and exit are both ALWAYS; the session may keep firing until management or restart rules stop it.');
    }

    if (entryType === 'HAS_POSITION' && exitType === 'HAS_POSITION') {
      warnings.push('Entry and exit both require a position; this can keep the engine in a waiting loop.');
    }

    if (entryType === 'NO_POSITION' && exitType === 'NO_POSITION') {
      warnings.push('Entry and exit both require no position; the strategy may never progress past the waiting state.');
    }

    if ((entryType === 'HAS_POSITION' && exitType === 'NO_POSITION') || (entryType === 'NO_POSITION' && exitType === 'HAS_POSITION')) {
      warnings.push('Entry and exit position rules point in opposite directions; double-check that this is intentional.');
    }

    if ((entryIsTime && exitIsThreshold) || (exitIsTime && entryIsThreshold)) {
      warnings.push('You are mixing time-based and threshold-based conditions; make sure the launch and exit rules are meant to compete together.');
    }

    if ((entryIsTime || exitIsTime) && hasManagementThresholds) {
      warnings.push('Time-based conditions are combined with management thresholds; the live engine will evaluate both, so confirm the threshold scope is intentional.');
    }

    if (conditions.management && (entryType === 'LOSS_THRESHOLD' || entryType === 'PROFIT_THRESHOLD')) {
      warnings.push('Management thresholds and entry thresholds are both active; ensure the duplicate limits are intentional.');
    }

    if (entryIsTime && exitIsTime) {
      warnings.push('Both entry and exit are time-based. That is valid, but it can leave the strategy idle until both clocks align.');
    }

    return warnings;
  }

  /**
   * Validate position manager
   */
  private validatePositionManager(positionManager: PositionManager): string[] {
    const errors: string[] = [];

    if (positionManager.maxPositions < 1) {
      errors.push('Max positions must be at least 1');
    }

    if (positionManager.stopLoss < 0 || positionManager.stopLoss > 100) {
      errors.push('Stop loss must be between 0 and 100');
    }

    if (positionManager.takeProfit < 0 || positionManager.takeProfit > 100) {
      errors.push('Take profit must be between 0 and 100');
    }

    return errors;
  }

  /**
   * Validate indicator configuration
   */
  private validateIndicators(indicators: Array<Record<string, unknown>>): string[] {
    const errors: string[] = [];
    const supportedOperators = new Set(['GT', 'LT', 'GTE', 'LTE', 'EQ', 'NEQ']);

    indicators.forEach((indicator, index) => {
      const type = String(indicator.type ?? '').trim();
      const left = String(indicator.left ?? '').trim();

      if (type) {
        const period = Number(indicator.period ?? 0);
        if (!type) {
          errors.push(`Indicator ${index + 1} type is required`);
        }
        if (!Number.isFinite(period) || period <= 0) {
          errors.push(`Indicator ${index + 1} period must be greater than 0`);
        }
        const symbol = String(indicator.symbol ?? '').trim();
        if (!symbol) {
          errors.push(`Indicator ${index + 1} symbol is required`);
        }
        return;
      }

      if (left) {
        const operator = String(indicator.operator ?? '').trim().toUpperCase();
        const threshold = Number(indicator.threshold ?? NaN);
        if (!supportedOperators.has(operator)) {
          errors.push(`Indicator ${index + 1} operator is invalid`);
        }
        if (!Number.isFinite(threshold)) {
          errors.push(`Indicator ${index + 1} threshold must be a number`);
        }
      }
    });

    return errors;
  }

  /**
   * Validate restart configuration
   */
  private validateRestart(restart: { condition?: { type?: string } | null; onWin?: { resetStake: number } | null; onLoss?: { resetStake: number } | null }): string[] {
    const errors: string[] = [];
    const restartType = String(restart.condition?.type ?? '').trim().toUpperCase();

    if (restart.condition) {
      const allowed = new Set(['AFTER_WIN', 'AFTER_LOSS', 'AFTER_WIN_OR_LOSS']);
      if (!restartType) {
        errors.push('Restart condition type is required');
      } else if (!allowed.has(restartType)) {
        errors.push('Restart condition type is invalid');
      }
    }

    if (restart.onWin && restart.onWin.resetStake <= 0) {
      errors.push('Restart on win stake must be greater than 0');
    }

    if (restart.onLoss && restart.onLoss.resetStake <= 0) {
      errors.push('Restart on loss stake must be greater than 0');
    }

    return errors;
  }

  /**
   * Check if contract type requires digit target
   */
  private requiresDigit(contractType: string): boolean {
    const digitTypes = ['MATCHES', 'DIFFERS', 'OVER', 'UNDER', 'EVEN', 'ODD', 'PRIME', 'NON_PRIME'];
    return digitTypes.includes(contractType);
  }

  /**
   * Check if contract type requires digit range
   */
  private requiresDigitRange(contractType: string): boolean {
    const digitTypes = ['RANGE_IN', 'RANGE_OUT'];
    return digitTypes.includes(contractType);
  }

  /**
   * Check if contract type requires barrier (single barrier)
   */
  private requiresBarrier(contractType: string): boolean {
    const barrierTypes = ['HIGH', 'LOW', 'KNOCK_IN', 'KNOCK_OUT'];
    return barrierTypes.includes(contractType);
  }

  /**
   * Check if contract type requires double barrier
   */
  private requiresDoubleBarrier(contractType: string): boolean {
    const barrierTypes = ['ENDS_BETWEEN', 'ENDS_OUTSIDE', 'STAYS_IN', 'STAYS_OUT'];
    return barrierTypes.includes(contractType);
  }

  /**
   * Validate strategy JSON before sending to API
   */
  // In validation.ts - update validateApiPayload

  validateApiPayload(payload: any): ApiValidationResult {
    const required = ['request', 'symbol', 'contract_type', 'stake', 'duration', 'duration_unit'];
    const missing = required.filter(field => !payload[field]);
    
    if (missing.length > 0) {
      return {
        valid: false,
        errors: [`Missing required fields: ${missing.join(', ')}`]
      };
    }

    const contractType = payload.contract_type;
    const errors: string[] = [];

    // Validate barrier fields based on contract type
    if (this.requiresBarrier(contractType)) {
      if (payload.barrier === undefined || payload.barrier === null) {
        errors.push(`Barrier is required for ${contractType}`);
      }
    }

    if (this.requiresDoubleBarrier(contractType)) {
      if (payload.barrier_low === undefined || payload.barrier_high === undefined) {
        errors.push(`Double barrier is required for ${contractType}`);
      }
      if (payload.barrier_low !== undefined && payload.barrier_high !== undefined) {
        if (payload.barrier_low >= payload.barrier_high) {
          errors.push('Lower barrier must be less than upper barrier');
        }
      }
    }

    if (this.requiresDigit(contractType)) {
      if (payload.digit_target === undefined || payload.digit_target === null) {
        errors.push(`Digit target is required for ${contractType}`);
      }
      if (payload.digit_target !== undefined) {
        if (payload.digit_target < 0 || payload.digit_target > 9) {
          errors.push('Digit target must be between 0 and 9');
        }
      }
    }

    if (this.requiresDigitRange(contractType)) {
      if (payload.digit_low === undefined || payload.digit_high === undefined) {
        errors.push(`Digit range is required for ${contractType}`);
      }
      if (payload.digit_low !== undefined && payload.digit_high !== undefined) {
        if (payload.digit_low >= payload.digit_high) {
          errors.push('Digit low must be less than digit high');
        }
        if (payload.digit_low < 0 || payload.digit_low > 8) {
          errors.push('Digit low must be between 0 and 8');
        }
        if (payload.digit_high < 1 || payload.digit_high > 9) {
          errors.push('Digit high must be between 1 and 9');
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export singleton
export const validationService = new ValidationService();
